/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import { Button, PasswordField } from '@coursera/cds-core';

import { NOT_EMPTY_CONSTRAINT } from 'bundles/authentication/constants';
import Form from 'bundles/authentication/shared/Form';
import Modal from 'bundles/authentication/shared/Modal';

import _t from 'i18n!nls/account-settings';

type Props = {
  description?: React.ReactNode;
  isOpen: boolean;
  onCancel?: () => void;
  onConfirm?: (password: string) => void;
  submit?: React.ReactNode;
  title?: React.ReactNode;
};

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 12px 16px;
  `,
  container: css`
    display: flex;
    flex-direction: column;
    gap: 24px;
  `,
  description: css`
    margin: 0;
  `,
  submit: css`
    max-width: none;
  `,
  title: css`
    color: #1f1f1f;
    font-family: 'Open Sans', sans-serif;
    font-size: 24px;
    line-height: 32px;
    margin: 0;

    @media (min-width: 600px) {
      font-size: 34px;
      line-height: 42px;
    }
  `,
  password: css`
    & label {
      margin-bottom: 0;
    }
  `,
};

const PasswordConfirmationModal = ({
  description = _t('We would like to confirm your password to continue.'),
  isOpen,
  onCancel,
  onConfirm = () => undefined,
  submit = _t('Log out from all devices'),
  title = _t('Confirmation'),
}: Props) => {
  const onSubmit = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    ({ password }) => {
      onConfirm(password);
    },
    [onConfirm]
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel}>
      <section css={styles.root}>
        <h2 css={styles.title}>{title}</h2>
        <p css={styles.description}>{description}</p>
        <Form css={styles.container} constraints={{ password: NOT_EMPTY_CONSTRAINT }} onSubmit={onSubmit}>
          <PasswordField
            name="password"
            label={_t('Password')}
            placeholder={_t('Password')}
            autoComplete="current-password"
            css={styles.password}
          />
          <Button css={styles.submit} variant="primary" type="submit">
            {submit}
          </Button>
        </Form>
      </section>
    </Modal>
  );
};

export default PasswordConfirmationModal;
